import React, { useState, useEffect, useRef } from "react";
import { FaTrash, FaCheckCircle, FaSpinner, FaExclamationCircle, FaEdit, FaBan, FaExclamationTriangle } from "react-icons/fa";
import { MdDescription, MdTag } from "react-icons/md";
import { useConfirmation } from "../common/ConfirmationContext";
import { useToast } from "../common/ToastContext";
import { modelService } from "../../services/modelService";
import { trainingDataIcon, fineTunedModelIcon, coreModelIcon } from "../common/IconLibrary";

const StatusBadge = ({ status }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded':
        return 'bg-green-500/50';
      case 'pending':
      case 'running':
        return 'bg-yellow-500';
      case 'failed':
        return 'bg-red-500';
      case 'cancelled':
        return 'bg-orange-500';
      case 'draft':
        return 'bg-gray-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'succeeded':
        return <FaCheckCircle className="mr-1" />;
      case 'pending':
      case 'running':
        return <FaSpinner className="mr-1 animate-spin" />;
      case 'failed':
        return <FaExclamationCircle className="mr-1" />;
      case 'cancelled':
        return <FaBan className="mr-1" />;
      case 'draft':
        return <FaEdit className="mr-1" />;
      default:
        return <FaExclamationTriangle className="mr-1" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'succeeded':
        return 'Processed';
      case 'pending':
        return 'Pending';
      case 'running':
        return 'Processing';
      case 'cancelled':
        return 'Cancelled';
      case 'draft':
        return 'Draft';
      case 'failed':
        return 'Failed';
      default:
        return status;
    }
  };

  return (
    <span className={`flex items-center px-2 py-1 rounded-full text-xs text-white ${getStatusColor(status)}`}>
      {getStatusIcon(status)}
      <span className="capitalize">{getStatusText(status)}</span>
    </span>
  );
};

const FineTunedModelEditor = ({ item, onSave, onClose, onCancel }) => {
  const { openConfirmation } = useConfirmation();
  const { showToast } = useToast();
  const [editedData, setEditedData] = useState(
    item || {
      name: `New fine tuned model ${Math.floor(1000 + Math.random() * 9000)}`,
      description: "",
      base_model: "gpt-4o",
      training_data_ids: [],
      tags: [],
    }
  );
  const [trainingDataOptions, setTrainingDataOptions] = useState([]);
  const isExistingModel = !!item;
  const isDraftState = isExistingModel && item.status === "draft";
  const modalRef = useRef(null);

  useEffect(() => {
    const fetchTrainingData = async () => {
      try {
        const data = await modelService.getTrainingDataList();
        setTrainingDataOptions(data);
      } catch (error) {
        console.error("Error fetching training data:", error);
        showToast("Failed to fetch training data options", "error");
      }
    };
    fetchTrainingData();
  }, [showToast]);

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        handleClose();
      }
    };
    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTagsChange = (e) => {
    setEditedData((prev) => ({
      ...prev,
      tags: e.target.value.split(",").map((tag) => tag.trim()),
    }));
  };

  const handleClose = () => {
    onClose();
  };

  const handleClickOutside = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      handleClose();
    }
  };

  const handleSubmit = async (e, startProcess = false) => {
    e.preventDefault();
    try {
      let savedModel;
      const updatedFields = {
        name: editedData.name,
        description: editedData.description,
        tags: editedData.tags,
        base_model: editedData.base_model,
      };
      if (isDraftState || !isExistingModel) {
        updatedFields.training_data_ids = editedData.training_data_ids;
      }
      if (isExistingModel) {
        savedModel = await modelService.updateFineTunedModelData(
          item.id,
          updatedFields,
          startProcess
        );
      } else {
        savedModel = await modelService.fineTuneModel(updatedFields, startProcess);
      }
      onSave(savedModel);
      showToast(
        startProcess
          ? "Fine-tuned model creation started"
          : "Model saved successfully",
        "success"
      );
      onClose();
    } catch (error) {
      console.error("Error saving fine-tuned model:", error);
      showToast("Failed to save fine-tuned model", "error");
    }
  };

  const handleCancelClick = () => {
    openConfirmation({
      title: "Cancel Fine-Tune Job",
      message:
        "Are you sure you want to cancel this fine-tune job? This action cannot be undone.",
      onConfirm: () => {
        onCancel(item.id);
      },
    });
  };

  const handleDeleteClick = () => {
    openConfirmation({
      title: "Delete Fine-Tuned Model",
      message: "Are you sure you want to delete this fine-tuned model? This action cannot be undone.",
      onConfirm: async () => {
        try {
          await modelService.deleteFineTunedModel(item.id);
          showToast("Fine-tuned model deleted successfully", "success");
          onClose();
        } catch (error) {
          console.error("Error deleting fine-tuned model:", error);
          showToast("Failed to delete fine-tuned model", "error");
        }
      },
    });
  };

  const isCreateDisabled =
    !editedData.name.trim() ||
    !editedData.description.trim() ||
    !editedData.base_model ||
    !(editedData.training_data_ids && editedData.training_data_ids.length > 0);

  const isDeleteEnabled = isExistingModel && ['draft', 'cancelled', 'processed', 'succeeded'].includes(item?.status);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center overflow-y-auto"
      onClick={handleClickOutside}
    >
      <div
        ref={modalRef}
        className="bg-gray-800 p-6 rounded-lg w-full max-w-4xl m-4"
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={(e) => handleSubmit(e, false)}>
          <div className="grid grid-cols-1 gap-4">
            <div className="relative">
              <div 
                className="absolute left-2 top-3 text-gray-400" 
                style={{ width: '20px', height: '20px' }}
                dangerouslySetInnerHTML={{ __html: fineTunedModelIcon }}
              />
              <input
                type="text"
                name="name"
                value={editedData.name}
                onChange={handleChange}
                className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
                placeholder="Fine-tuned model name"
                required
              />
            </div>
            <div className="relative">
              <MdDescription className="absolute left-2 top-3 text-gray-400" />
              <textarea
                name="description"
                value={editedData.description}
                onChange={handleChange}
                className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
                placeholder="Description"
                rows="3"
              />
            </div>
            <div className="relative">
              <MdTag className="absolute left-2 top-3 text-gray-400" />
              <input
                type="text"
                name="tags"
                value={editedData.tags.join(", ")}
                onChange={handleTagsChange}
                className="w-full pl-8 p-2 bg-gray-700 text-white rounded"
                placeholder="Tags (comma separated)"
              />
            </div>
            <div className="relative">
              <div 
                className="absolute left-2 top-3 text-gray-400" 
                style={{ width: '20px', height: '20px' }}
                dangerouslySetInnerHTML={{ __html: coreModelIcon }}
              />
              <select
                name="base_model"
                value={editedData.base_model}
                onChange={handleChange}
                className="w-full pl-8 p-2 bg-gray-700 text-white rounded appearance-none"
                disabled={isExistingModel && !isDraftState}
              >
                <option value="gpt-4o">GPT-4o</option>
              </select>
            </div>
            <div className="relative">
              <div 
                className="absolute left-2 top-3 text-gray-400" 
                style={{ width: '20px', height: '20px' }}
                dangerouslySetInnerHTML={{ __html: trainingDataIcon }}
              />
              <select
                name="training_data_ids"
                value={
                  editedData.training_data_ids &&
                  editedData.training_data_ids.length > 0
                    ? editedData.training_data_ids[0]
                    : ""
                }
                onChange={(e) =>
                  setEditedData((prev) => ({
                    ...prev,
                    training_data_ids: e.target.value ? [e.target.value] : [],
                  }))
                }
                className="w-full pl-8 p-2 bg-gray-700 text-white rounded appearance-none"
                required
                disabled={isExistingModel && !isDraftState}
              >
                <option value="">Select Training Data</option>
                {trainingDataOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </select>
            </div>
            {isExistingModel && item.status && (
              <div className="flex items-center">
                <span className="text-gray-300 mr-2">Status:</span>
                <StatusBadge status={item.status} />
              </div>
            )}
          </div>
          <div className="mt-8 flex justify-between items-center">
            <div>
              {isDeleteEnabled && (
                <button
                  type="button"
                  onClick={handleDeleteClick}
                  className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-500"
                >
                  <FaTrash className="inline mr-2" />
                  Delete
                </button>
              )}
            </div>
            <div className="flex items-center">
              <button
                type="button"
                onClick={handleClose}
                className="bg-gray-600 text-white px-4 py-2 rounded mr-2 hover:bg-gray-500"
              >
                Close
              </button>
              {isExistingModel &&
                (item.status === "pending" || item.status === "running") && (
                  <button
                    type="button"
                    onClick={handleCancelClick}
                    className="bg-orange-600 text-white px-4 py-2 rounded mr-2 hover:bg-orange-500"
                  >
                    Cancel Job
                  </button>
                )}
              <button
                type="submit"
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-500 mr-2"
              >
                {isExistingModel ? "Save" : "Save as Draft"}
              </button>
              {(!isExistingModel || isDraftState) && (
                <button
                  type="button"
                  onClick={(e) => handleSubmit(e, true)}
                  className={`bg-green-600 text-white px-4 py-2 rounded ${
                    isCreateDisabled
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:bg-green-500"
                  }`}
                  disabled={isCreateDisabled}
                >
                  Create
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FineTunedModelEditor;